
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useFetchOne } from '../../hooks/useFetch';
import { getMediaURL, formatDate } from '../../utils/helpers';
import Loading from '../../components/common/Loading';
import ErrorMessage from '../../components/common/ErrorMessage';
import ReactMarkdown from 'react-markdown';
import { FiFileText, FiDownload } from 'react-icons/fi';
import SocialShares from '../../components/common/Socials/SocialShares';
import { API_ENDPOINTS, URL_ENDPOINTS, POPULATE_PARAMS } from '../../config/constants';
import { toTurkishDate } from '../../utils/dateConvertToTr'
const MagazineDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { data: magazine, loading, error } = useFetchOne(API_ENDPOINTS.MAGAZINES, slug, POPULATE_PARAMS.ONE_WITH_IMAGES);

    if (loading) return <Loading />;
    if (error) return <ErrorMessage message={error} />;
    if (!magazine) {
        return (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <ErrorMessage message="İçerik bulunamadı" />
                <div className="text-center mt-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="px-8 py-4 bg-blue-900 text-white rounded font-semibold hover:bg-blue-800 transition-colors"
                    >
                        ← Geri Dön
                    </button>
                </div>
            </div>
        );
    }

    // const imageUrl = getMediaURL(magazine.image);
    const imageUrl = magazine?.image
        ? getMediaURL(magazine.image)
        : '/placeholder_hor.jpg';
    const pdfUrl = getMediaURL(magazine.pdf);

    return (
        <div className="min-h-screen">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
                {/* Breadcrumb */}
                <nav className="mb-8 text-sm text-gray-600">
                    <Link to="/" className="text-blue-900 hover:underline">Ana Sayfa</Link>
                    <span className="mx-2 text-gray-400">/</span>
                    <Link to={`/${URL_ENDPOINTS.MAGAZINES}`} className="text-blue-900 hover:underline">Dergiler</Link>
                    <span className="mx-2 text-gray-400">/</span>
                    <span className="text-gray-800">{magazine.title}</span>
                </nav>

                {/* Two Column Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                    {/* Left Column - Image and Preview Button */}
                    <div className="lg:col-span-1">
                        <div className="lg:sticky lg:top-24">
                            {/* Image Card - Standard book aspect ratio */}
                            {imageUrl && (
                                <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6 max-w-xs mx-auto">
                                    {/* Adjust aspect ratio here: aspect-[2/3] for standard book (width/height) */}
                                    <div className="aspect-[1/1.3] w-full">
                                        <img
                                            src={Array.isArray(imageUrl) ? imageUrl[0] : imageUrl}
                                            alt={magazine.title}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Preview Button */}
                            {pdfUrl && (
                                <a
                                    href={Array.isArray(pdfUrl) ? pdfUrl[0] : pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block w-full"
                                >
                                    <button className="w-full px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors">
                                        Yayını indir
                                    </button>
                                </a>
                            )}
                        </div>
                    </div>

                    {/* Right Column - Content */}
                    <div className="lg:col-span-2">
                        {/* Title */}
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                            {magazine.title}
                        </h1>

                        {/* Subtitle/Category */}
                        <p className="text-lg text-gray-500 uppercase tracking-wide mb-6">
                            DERGİ
                        </p>

                        {/* Description */}
                        <div className="prose prose-lg max-w-none text-gray-700 mb-8 leading-relaxed">
                            <ReactMarkdown
                                components={{
                                    img: ({ node, ...props }) => (
                                        <img
                                            {...props}
                                            className="max-w-full h-auto block my-4 rounded"
                                            alt={props.alt || ''}
                                        />
                                    ),
                                    p: ({ node, ...props }) => (
                                        <p {...props} className="mb-4" />
                                    ),
                                    h1: ({ node, ...props }) => (
                                        <h1 {...props} className="text-2xl sm:text-3xl font-bold mt-8 mb-4" />
                                    ),
                                    h2: ({ node, ...props }) => (
                                        <h2 {...props} className="text-xl sm:text-2xl font-bold mt-6 mb-3" />
                                    ),
                                    h3: ({ node, ...props }) => (
                                        <h3 {...props} className="text-lg sm:text-xl font-bold mt-4 mb-2" />
                                    ),
                                    ul: ({ node, ...props }) => (
                                        <ul {...props} className="list-disc list-inside mb-4 space-y-2" />
                                    ),
                                    ol: ({ node, ...props }) => (
                                        <ol {...props} className="list-decimal list-inside mb-4 space-y-2" />
                                    ),
                                    a: ({ node, ...props }) => (
                                        <a {...props} className="text-blue-900 hover:underline" />
                                    ),
                                }}
                            >
                                {magazine.description}
                            </ReactMarkdown>
                        </div>

                        {/* Full Content */}
                        {magazine.content && (
                            <div className="text-base sm:text-lg leading-relaxed text-gray-700 mb-8">
                                <p>{magazine.content}</p>
                            </div>
                        )}

                        {/* Date */}
                        {magazine.publishedAt && (
                            <p className="text-lg text-gray-400 mb-8">
                                {toTurkishDate(magazine.publishedAt)}
                            </p>
                        )}

                        {/* PDF Download Link */}
                        {/* {pdfUrl && (
                            <div className="mb-8">
                                <a
                                    href={Array.isArray(pdfUrl) ? pdfUrl[0] : pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-3 text-blue-900 hover:text-blue-700 font-medium transition-colors"
                                >
                                    <FiDownload className="w-5 h-5" />
                                    <span>Yayını indir</span>
                                </a>
                            </div>
                        )} */}

                        {/* Back Button */}
                        <button
                            onClick={() => navigate(-1)}
                            className="px-6 py-3 bg-blue-900 text-white rounded font-medium hover:bg-blue-800 transition-colors"
                        >
                            ← Geri Dön
                        </button>
                    </div>
                </div>

                {/* Social Shares */}
                <div className="mt-12">
                    <SocialShares />
                </div>
            </div>
        </div>
    );
};

export default MagazineDetail;